"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.asInferredNodes = exports.asInferredNode = exports.createNestedNodeId = void 0;
exports.resetChildUidsAndRootNode = resetChildUidsAndRootNode;
exports.getSubTreeUids = getSubTreeUids;
exports.filterOrphanedNodes = filterOrphanedNodes;
exports.filterValidNodes = filterValidNodes;
exports.constructNodeUidForSubLayout = constructNodeUidForSubLayout;
const shared_1 = require("@buildwithflux/shared");
const factory_1 = require("./node/factory");
const structure_1 = require("./node/structure");
/**
 * Takes a map of PcbNodes, pretend there's no childUids under any node, then
 * re-add the root and re-compute childUids for every node from scratch,
 * including the root node.
 */
function resetChildUidsAndRootNode(pcbNodes) {
    if (!pcbNodes[structure_1.pcbLayoutRootNodeId]) {
        pcbNodes[structure_1.pcbLayoutRootNodeId] = (0, factory_1.createRootNode)();
    }
    const childrenUidsMap = {};
    for (const node of Object.values(pcbNodes)) {
        if (!(node.uid in childrenUidsMap)) {
            // Ensure every node has an entry, not just nodes with children.
            // Simplifies code that uses the index.
            childrenUidsMap[node.uid] = [];
        }
        const parentUid = node.parentUid;
        if (!(parentUid in childrenUidsMap)) {
            childrenUidsMap[parentUid] = [node.uid];
        }
        else {
            childrenUidsMap[parentUid].push(node.uid);
        }
    }
    const pcbNodesWithChildUids = {};
    // It is important to maintain the order of `pcbNodes` object
    // with `childUids`
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    Object.keys(pcbNodes).forEach((nodeUid) => {
        const node = pcbNodes[nodeUid];
        if (node) {
            pcbNodesWithChildUids[nodeUid] = {
                ...node,
                // From code above, each nodeUid should appear as key in `childrenUidsMap`
                childUids: childrenUidsMap[nodeUid],
            };
        }
    });
    return pcbNodesWithChildUids;
}
/**
 * Find uids of all nodes in the subtree
 *
 * @returns an array containing uids of all decendants. Please note that this result does NOT contain the
 * passed in nodeUid
 */
function getSubTreeUids(params) {
    const { pcbNodes, nodeUid, includeRoot } = params;
    const result = [];
    const childUids = pcbNodes[nodeUid]?.childUids;
    if (includeRoot) {
        result.push(nodeUid);
    }
    if (childUids) {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        childUids.forEach((uid) => {
            result.push(uid);
            result.push(...getSubTreeUids({
                pcbNodes,
                nodeUid: uid,
            }));
        });
    }
    return result;
}
function isOrphanedNode(node, allNodes) {
    const isOrphaned = 
    // NOTE: exclude the root node AND the top level nodes because legacy documents
    // or parts may not have a root node.
    node.parentUid !== structure_1.pcbLayoutRootNodeParentId &&
        node.parentUid !== structure_1.pcbLayoutRootNodeId &&
        !(node.parentUid in allNodes);
    if (isOrphaned && (0, shared_1.isDevEnv)() && !(0, shared_1.areWeTestingWithJest)()) {
        // eslint-disable-next-line no-console
        console.error("Orphaned nodes detected", node);
    }
    return isOrphaned;
}
/**
 * Filters out all orphaned nodes in the given nodes map, and return a
 * new map with valid nodes
 *
 * We say a node is orphaned when
 * - it's parentUid is `__corrupt__`, or more general, when its parentUid does not exist in the map
 * - (recursively), any of its parent/ancestor node is orphaned
 *
 * Use this function to filter potential orphaned nodes when you are loading a (sub) tree. For
 * example:
 * - When first loading the project and hence the whole pcbLayoutNodes map
 * - When adding a part, which adds a node sub-tree to the project
 *
 * Time Complexity:
 * The worst case here is O(n^2) where the root node is orphaned and every node
 * in the map has one single child - super rare and this means something really buggy.
 */
function filterOrphanedNodes(nodes) {
    let validNodesSoFar = nodes;
    const validNodesMapSoFar = (0, shared_1.arrayToMapByUid)(validNodesSoFar);
    let orphanedNodes = validNodesSoFar.filter((node) => isOrphanedNode(node, validNodesMapSoFar));
    while (orphanedNodes.length > 0) {
        // First, remove orphaned nodes from both the array and the map
        validNodesSoFar = validNodesSoFar.filter((node) => !isOrphanedNode(node, validNodesMapSoFar));
        for (const node of orphanedNodes) {
            delete validNodesMapSoFar[node.uid];
        }
        // Then find new orphaned nodes (if any) after those from previous iteration are deleted
        orphanedNodes = validNodesSoFar.filter((node) => isOrphanedNode(node, validNodesMapSoFar));
    }
    return validNodesMapSoFar;
}
/**
 * NOTE: This is a debug function created to filter out corrupted node data.
 * The purpose is to unblock users and also allow us opening corresponding project to
 * investigate. Once the underlying issue is resolved, should remove call sites of this function
 *
 * Use this to unblock and debug this issue: https://github.com/buildwithflux/flux-app/issues/5945
 *
 * Marking it as deprecated as a signal to avoid any mis-use
 *
 * @deprecated
 */
function filterValidNodes(nodes) {
    return nodes.filter((node) => Boolean(node.type));
}
const createNestedNodeId = (existingNodeId, elementId) => {
    if (existingNodeId === undefined) {
        return ``;
    }
    if (existingNodeId.startsWith(elementId)) {
        return existingNodeId;
    }
    return constructNodeUidForSubLayout(elementId, existingNodeId);
};
exports.createNestedNodeId = createNestedNodeId;
const asInferredNode = (node) => {
    return node;
};
exports.asInferredNode = asInferredNode;
const asInferredNodes = (nodes) => {
    return nodes;
};
exports.asInferredNodes = asInferredNodes;
/**
 * Construct an uid for a sublayout node.
 *
 * We construct it by <parentUid>__<nodeUid>, we use this format to not only indicate
 * that this node is under a sublayout, but also allow "scoping" the sub layout node
 * under its ancestors' context
 *
 * @param {string} parentUid - a prefix string containing all parent nested parts
 * @param {string} nodeUid - the original uid of this node when its defined in the part
 *
 * @returns the result node uid for sub layout
 */
function constructNodeUidForSubLayout(parentUid, nodeUid) {
    return `${parentUid}${structure_1.pcbLayoutNestedNodeDelimiter}${nodeUid}`;
}
//# sourceMappingURL=functions.js.map